import { useState } from "react";
import { Card } from "../../../components/Card";
import { Switch } from "../../../components/Switch";
import { useDeletePricingRule, useUpdatePricingConfig } from "../hooks";
import type { PricingRule } from "../types";
import { ACTION_KEYS } from "../types";
import { RuleModal } from "./RuleModal";

function formatTimeScope(rule: PricingRule): string {
  const ts = rule.timeScope;
  if (!ts) return "Always";
  if (ts.date) return ts.date;
  if (ts.startDate && ts.endDate) return `${ts.startDate} — ${ts.endDate}`;
  if (ts.startDate) return `From ${ts.startDate}`;
  if (ts.endDate) return `Until ${ts.endDate}`;
  if (ts.days && ts.days.length > 0) return ts.days.join(", ");
  return "Always";
}

function formatActions(actions: Record<string, number>): string {
  return Object.entries(actions)
    .map(([key, val]) => {
      const def = ACTION_KEYS.find((a) => a.key === key);
      return `${def?.label || key}: ${val}`;
    })
    .join(", ");
}

export function RulesCard({
  rules,
  userId,
}: {
  rules: PricingRule[];
  userId: string;
}) {
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const deleteMutation = useDeletePricingRule();
  const updateMutation = useUpdatePricingConfig();

  const handleToggle = (rule: PricingRule) => {
    updateMutation.mutate({
      userId,
      data: {
        scope: { type: "rule", ruleId: rule.id },
        rule: { enabled: !rule.enabled },
        reasoning: `${rule.enabled ? "Disabled" : "Enabled"} rule: ${rule.label || rule.id}`,
        trigger: "admin",
      },
    });
  };

  const handleDelete = (rule: PricingRule) => {
    if (!window.confirm(`Delete rule "${rule.label || rule.id}"?`)) return;
    deleteMutation.mutate({ userId, ruleId: rule.id });
  };

  return (
    <>
      <Card className="!p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-content-primary">
            Rules
          </h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded hover:opacity-90"
          >
            + Add Rule
          </button>
        </div>

        {rules.length === 0 ? (
          <p className="text-sm text-content-tertiary py-4 text-center">
            No rules configured
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between px-3 py-2.5 border border-border-secondary rounded-lg"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Switch
                    checked={rule.enabled}
                    onChange={() => handleToggle(rule)}
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-content-primary">
                      {rule.label || "Untitled Rule"}
                    </span>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">
                        P{rule.priority}
                      </span>
                      <span className="text-xs text-content-tertiary">
                        {formatTimeScope(rule)}
                      </span>
                      {rule.conditions && rule.conditions.length > 0 && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded font-medium">
                          {rule.conditions.length} condition
                          {rule.conditions.length > 1 ? "s" : ""}
                        </span>
                      )}
                      <span className="text-xs text-content-tertiary truncate">
                        {formatActions(rule.actions)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 ml-2">
                  <button
                    onClick={() => setEditingRule(rule)}
                    className="px-2.5 py-1 text-xs font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(rule)}
                    disabled={deleteMutation.isPending}
                    className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {showAddModal && (
        <RuleModal userId={userId} onClose={() => setShowAddModal(false)} />
      )}

      {editingRule && (
        <RuleModal
          rule={editingRule}
          userId={userId}
          onClose={() => setEditingRule(null)}
        />
      )}
    </>
  );
}
